/**
 * The board's starting state, and the reset that puts it back.
 *
 * `reconcile(db)` is the only writer of seed data, and it is both the first seed and the nightly
 * reset: it reads every table in primary-key order, deletes each row a visitor made (their ids
 * start with `u-`), and writes back each seeded row that is missing or differs. So a reset writes
 * only what visitors touched, and running it twice in a row writes nothing the second time.
 *
 * The seed is deterministic — the same rows every time, timestamped back from a fixed epoch — so
 * "differs from the seed" is a comparison and not a judgement.
 */
import { isoTx } from 'r2db';
import type { MutationOp } from 'r2db';

import { q } from './queries.ts';
import { ISSUE_PRIORITIES } from './schema.ts';
import type { ISSUE_STATUSES } from './schema.ts';

export const SEED_ISSUES = 5000;
/**
 * How many projects the LOCAL seed makes, and why it is not the hosted number.
 *
 * The deployed demo's base is built offline (`rust/rindle-paged/examples/build_issues_store.rs`)
 * and holds thousands of projects, because its project count is derived from 20 GiB and the read
 * budget. This path exists for `pnpm local:setup` and the tests, where the point is that every
 * shape has more than one project to be wrong about — so it is small, and the ratio rather than
 * the size is what matches.
 */
export const SEED_PROJECTS = 12;
/** Seeded issues are spread back from here, seven minutes apart: about 24 days of history. */
const SEED_EPOCH = Date.UTC(2026, 9, 1);
const at = (n: number) => SEED_EPOCH - (SEED_ISSUES - n) * 7 * 60_000;
const pad = (n: number) => String(n).padStart(6, '0');

export const PEOPLE = [
  { id: 'amara', name: 'Amara Okafor' },
  { id: 'li', name: 'Li Wei' },
  { id: 'rohan', name: 'Rohan Kapoor' },
  { id: 'elena', name: 'Elena Soto' },
  { id: 'taro', name: 'Taro Nakamura' },
];
const BOT = { id: 'r2db-bot', name: 'r2db bot' };
export const TAGS = ['api', 'docs', 'perf', 'query', 'sync', 'ux', 'billing', 'console'];

const SUBJECTS = ['Live query', 'Subscribe socket', 'Lease refresh', 'Console data browser', 'Slow query log', 'Billing page',
  'CLI create', 'Schema diff', 'Write receipts', 'Transact retries', 'Index advice', 'Page cache', 'Signup form', 'Docs search'];
const SYMPTOMS = ['drops the first delta after a reconnect', 'shows a stale row until refresh', 'times out on a cold object',
  'double-counts a retried request', 'loses focus mid-edit', 'flickers on load', 'sorts equal timestamps unstably',
  'needs a clearer error message', 'ignores the declared index', 'is slow on a large table', 'renders badly on a phone'];
const DESCRIPTIONS = [
  'Steps to reproduce are in the thread. It happens about one time in five.',
  'Seen in production yesterday afternoon. No errors in the log, which is the worrying part.',
  'Low risk, but people keep tripping on it. A small fix should do.',
  'Probably a missing index. Worth checking the slow query log before guessing.',
  'Customer report, paraphrased. Needs a repro before anyone picks it up.',
];

type Row = Record<string, string | number>;
const TABLES = ['project', 'user', 'issue', 'comment', 'tag'] as const;

/** The projects, and the key each one's issues are shown under. */
const PROJECT_NAMES = [
  ['API', 'Public API'], ['CON', 'Console'], ['DOC', 'Documentation'], ['ENG', 'Engine'],
  ['BIL', 'Billing'], ['CLI', 'Command line'], ['WEB', 'Marketing site'], ['SDK', 'Client SDKs'],
  ['OPS', 'Operations'], ['SEC', 'Security'], ['PER', 'Performance'], ['SUP', 'Support'],
];
type Table = (typeof TABLES)[number];

const REPLIES = [
  'I can reproduce this on a fresh database. Taking a look.',
  'Is this the same thing as last week, or new?',
  'Added a failing test. The fix is smaller than it looked.',
  'Checked the slow query log: the filter is not covered. Declaring an index fixes it.',
  'Not seeing it here. Which region was the object in?',
  'Fixed on main. Leaving this open until it ships.',
  'This only happens after a deploy, which narrows it down a lot.',
  'Moving this to review — the change is up.',
  'Can we get a screenshot? I cannot tell which view this is.',
  'Bumping priority; two more reports today.',
  'Confirmed the fix against the original repro.',
  'Duplicate of an older one, but this has the better description.',
];
/** Status, weighted the way a real tracker skews: a lot done, a little in review. */
const STATUS_WEIGHTS: [typeof ISSUE_STATUSES[number], number][] = [['backlog', 18], ['todo', 20], ['in-progress', 14], ['review', 10], ['done', 38]];

/** A small deterministic hash, so every attribute of seed issue `n` is independent of the others
 *  and of `n` itself — one counter driving them all put every board column in one owner's name. */
function mix(n: number, salt: number): number {
  let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(salt + 1, 0xc2b2ae35);
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d); h ^= h >>> 15; h = Math.imul(h, 0x846ca68b); h ^= h >>> 16;
  return (h >>> 0) / 0x1_0000_0000;
}
const pick = <T>(list: readonly T[], n: number, salt: number) => list[Math.floor(mix(n, salt) * list.length)];
function weighted<T>(weights: [T, number][], n: number, salt: number): T {
  const total = weights.reduce((sum, [, w]) => sum + w, 0);
  let at = mix(n, salt) * total;
  for (const [value, w] of weights) { if ((at -= w) < 0) return value; }
  return weights[weights.length - 1][0];
}

/** Every seeded row, by table and then by id. */
export function seedRows(count = SEED_ISSUES, projectCount = SEED_PROJECTS): Record<Table, Map<string, Row>> {
  const rows: Record<Table, Map<string, Row>> = { project: new Map(), user: new Map(), issue: new Map(), comment: new Map(), tag: new Map() };
  const projects = PROJECT_NAMES.slice(0, Math.max(1, Math.min(projectCount, PROJECT_NAMES.length)))
    .map(([key, name], n) => ({ id: `seed-proj-${pad(n)}`, name, key }));
  for (const project of projects) rows.project.set(project.id, { ...project });
  for (const person of [...PEOPLE, BOT]) rows.user.set(person.id, { ...person });
  for (let n = 0; n < count; n++) {
    const id = `seed-${pad(n)}`;
    const created = at(n);
    // A discussion on about two issues in five, one to four replies, an hour or more apart, by
    // the cast. The issue's `updatedAt` is its last reply, as a write through the app would leave it.
    const replies = mix(n, 7) < 0.4 ? 1 + Math.floor(mix(n, 8) * 4) : 0;
    let updated = created;
    for (let r = 0; r < replies; r++) {
      updated = created + (r + 1) * (60 + Math.floor(mix(n * 8 + r, 9) * 600)) * 60_000;
      rows.comment.set(`${id}-c${r}`, { id: `${id}-c${r}`, issueId: id, authorId: pick(PEOPLE, n * 8 + r, 10).id, body: pick(REPLIES, n * 8 + r, 11), createdAt: updated });
    }
    rows.issue.set(id, {
      id, projectId: pick(projects, n, 14).id,
      title: `${pick(SUBJECTS, n, 1)} ${pick(SYMPTOMS, n, 2)}`,
      status: weighted(STATUS_WEIGHTS, n, 3), priority: pick(ISSUE_PRIORITIES, n, 4),
      ownerId: pick(PEOPLE, n, 5).id, createdAt: created, updatedAt: updated,
    });
    rows.comment.set(`${id}-d`, { id: `${id}-d`, issueId: id, authorId: BOT.id, body: pick(DESCRIPTIONS, n, 6), createdAt: created });
    const tagCount = 1 + Math.floor(mix(n, 12) * 3);
    const first = Math.floor(mix(n, 13) * TAGS.length);
    for (let t = 0; t < tagCount; t++) {
      rows.tag.set(`${id}-t${t}`, { id: `${id}-t${t}`, issueId: id, name: TAGS[(first + t * 3) % TAGS.length] });
    }
  }
  return rows;
}

/** The part of the r2db client this needs, so a test can hand it a fake. */
export interface SeedClient {
  exec(query: any): Promise<unknown>;
  write(ops: readonly MutationOp[]): Promise<unknown>;
}

const PAGE = 500;
const WRITE_BATCH = 100;

/** Every row of one table, in primary-key order, a page at a time. */
async function readAll(db: SeedClient, table: Table): Promise<Map<string, Row>> {
  const found = new Map<string, Row>();
  let after: string | undefined;
  for (;;) {
    let query: any = (q as any)[table].orderBy('id', 'asc');
    if (after !== undefined) query = query.start({ id: after }, { exclusive: true });
    const page = await db.exec(query.limit(PAGE)) as Row[];
    for (const row of page) found.set(String(row.id), row);
    if (page.length < PAGE) return found;
    after = String(page[page.length - 1].id);
  }
}

const same = (a: Row, b: Row) => Object.keys(b).every(k => a[k] === b[k]);

export interface ReconcileReport { deleted: Record<Table, number>; restored: Record<Table, number>; writes: number; ms: number }

/** Put the database back to the seed. Safe to run at any time, and idempotent. */
export async function reconcile(db: SeedClient, count = SEED_ISSUES): Promise<ReconcileReport> {
  const started = Date.now();
  const want = seedRows(count);
  const ops: MutationOp[] = [];
  const zero = (): Record<Table, number> => ({ project: 0, user: 0, issue: 0, comment: 0, tag: 0 });
  const report: ReconcileReport = { deleted: zero(), restored: zero(), writes: 0, ms: 0 };
  for (const table of TABLES) {
    const have = await readAll(db, table);
    // Anything not in the seed goes: every row a visitor made, and every user they invented.
    for (const id of have.keys()) {
      if (!want[table].has(id)) { ops.push(isoTx.delete(table as any, { id } as any)); report.deleted[table]++; }
    }
    for (const [id, row] of want[table]) {
      const current = have.get(id);
      if (!current || !same(current, row)) { ops.push(isoTx.upsert(table as any, row as any)); report.restored[table]++; }
    }
  }
  for (let i = 0; i < ops.length; i += WRITE_BATCH) {
    await db.write(ops.slice(i, i + WRITE_BATCH));
    report.writes++;
  }
  report.ms = Date.now() - started;
  return report;
}
